import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { MiniGameService } from '../../../_services/mini-game.service';
import { MiniGameSession, TugOfWarState, StudentTeam } from '../../../interfaces/mini-game.interface';
import { GameQuestion } from '../../../interfaces/question.interface';
import { GlobalService } from 'src/app/_services/global.service';
import { GameAudioService } from '../../../_services/game-audio.service';

type TugPhase = 'playing' | 'round-result' | 'final';

@Component({
  selector: 'app-tug-of-war-game',
  templateUrl: './tug-of-war-game.component.html',
  styleUrls: ['./tug-of-war-game.component.scss']
})
export class TugOfWarGameComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  currLang: any = 'en';

  session: MiniGameSession | null = null;
  internalPhase: TugPhase = 'playing';
  timerValue = 0;
  showTeamADropdown = false;
  showTeamBDropdown = false;

  // Team A state
  teamAQuestion: GameQuestion | null = null;
  teamASelectedOption: 'A' | 'B' | 'C' | 'D' | null = null;
  teamASubmitted = false;
  teamAResult: { isCorrect: boolean; xpEarned: number } | null = null;

  // Team B state
  teamBQuestion: GameQuestion | null = null;
  teamBSelectedOption: 'A' | 'B' | 'C' | 'D' | null = null;
  teamBSubmitted = false;
  teamBResult: { isCorrect: boolean; xpEarned: number } | null = null;

  isPulling = false;
  showInfo = false;
  private pendingTimeouts: any[] = [];
  private destroyed = false;

  get state(): TugOfWarState | null {
    return this.session?.gameState as TugOfWarState || null;
  }

  get teamA(): StudentTeam | null {
    return this.session?.teams?.teamA || null;
  }

  get teamB(): StudentTeam | null {
    return this.session?.teams?.teamB || null;
  }

  get teamAName(): string {
    if (!this.session?.teams) return '';
    return this.currLang === 'ar' ? this.session.teams.teamA.name.ar : this.session.teams.teamA.name.en;
  }

  get teamBName(): string {
    if (!this.session?.teams) return '';
    return this.currLang === 'ar' ? this.session.teams.teamB.name.ar : this.session.teams.teamB.name.en;
  }

  constructor(
    private miniGameService: MiniGameService,
    private globalService: GlobalService,
    private gameAudioService: GameAudioService
  ) {}

  ngOnInit(): void {
    this.globalService.getLang()
      .pipe(takeUntil(this.destroy$))
      .subscribe(lang => this.currLang = lang);

    this.miniGameService.session$
      .pipe(takeUntil(this.destroy$))
      .subscribe(session => {
        this.session = session;
        if (session) {
          this.loadRoundQuestions();
        }
      });

    this.miniGameService.timerValue$
      .pipe(takeUntil(this.destroy$))
      .subscribe(val => {
        this.timerValue = val;
        if (val <= 0 && this.internalPhase === 'playing') {
          this.onTimerExpired();
        }
      });

    this.startRound();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.pendingTimeouts.forEach(t => clearTimeout(t));
    this.pendingTimeouts = [];
    this.destroy$.next();
    this.destroy$.complete();
    this.miniGameService.stopTimer();
    this.stopWarDrums();
  }

  private safeTimeout(fn: () => void, ms: number): void {
    const id = setTimeout(() => {
      if (!this.destroyed) fn();
    }, ms);
    this.pendingTimeouts.push(id);
  }

  private warDrumsAudio: HTMLAudioElement | null = null;

  private startRound(): void {
    this.internalPhase = 'playing';
    this.teamASelectedOption = null;
    this.teamBSelectedOption = null;
    this.teamASubmitted = false;
    this.teamBSubmitted = false;
    this.teamAResult = null;
    this.teamBResult = null;
    this.isPulling = false;
    this.loadRoundQuestions();

    // Start war drums on first round (loops continuously)
    if (!this.warDrumsAudio) {
      this.playWarDrums();
    }

    const timeLimit = this.session?.config.timePerQuestion || 25;
    this.miniGameService.startTimer(timeLimit);
  }

  private playWarDrums(): void {
    this.warDrumsAudio = this.gameAudioService.play('assets/game-mode/sound-effects/warDrums.mp3', { loop: true });
  }

  private stopWarDrums(): void {
    this.gameAudioService.stop(this.warDrumsAudio);
    this.warDrumsAudio = null;
  }

  private loadRoundQuestions(): void {
    if (!this.session || !this.state) return;
    const round = this.state.currentRound;
    const aIdx = round * 2;
    const bIdx = round * 2 + 1;
    this.teamAQuestion = this.session.questions[aIdx] || null;
    this.teamBQuestion = this.session.questions[bIdx] || null;
  }

  selectOptionA(optionId: 'A' | 'B' | 'C' | 'D'): void {
    if (this.teamASubmitted) return;
    this.teamASelectedOption = optionId;
  }

  selectOptionB(optionId: 'A' | 'B' | 'C' | 'D'): void {
    if (this.teamBSubmitted) return;
    this.teamBSelectedOption = optionId;
  }

  submitTeamA(): void {
    if (!this.teamASelectedOption || this.teamASubmitted || !this.state) return;
    const round = this.state.currentRound;
    const qIdx = round * 2;
    this.teamAResult = this.miniGameService.submitTugOfWarTeamAnswer('A', this.teamASelectedOption, qIdx);
    this.teamASubmitted = true;
    this.isPulling = true;

    if (this.teamBSubmitted) {
      this.onBothSubmitted();
    }
  }

  submitTeamB(): void {
    if (!this.teamBSelectedOption || this.teamBSubmitted || !this.state) return;
    const round = this.state.currentRound;
    const qIdx = round * 2 + 1;
    this.teamBResult = this.miniGameService.submitTugOfWarTeamAnswer('B', this.teamBSelectedOption, qIdx);
    this.teamBSubmitted = true;
    this.isPulling = true;

    if (this.teamASubmitted) {
      this.onBothSubmitted();
    }
  }

  private onTimerExpired(): void {
    // Auto-submit for teams that haven't answered
    if (!this.teamASubmitted && this.state) {
      const round = this.state.currentRound;
      this.teamAResult = this.miniGameService.submitTugOfWarTeamAnswer('A', null, round * 2);
      this.teamASubmitted = true;
    }
    if (!this.teamBSubmitted && this.state) {
      const round = this.state.currentRound;
      this.teamBResult = this.miniGameService.submitTugOfWarTeamAnswer('B', null, round * 2 + 1);
      this.teamBSubmitted = true;
    }
    this.onBothSubmitted();
  }

  private onBothSubmitted(): void {
    this.miniGameService.stopTimer();
    this.internalPhase = 'round-result';
    this.isPulling = true;

    this.safeTimeout(() => {
      this.isPulling = false;
      this.miniGameService.advanceTugOfWarRound();

      if (this.state && this.state.currentRound < this.state.totalRounds) {
        this.startRound();
      } else {
        this.stopWarDrums();
      }
    }, 2000);
  }

  getRoundNum(): number {
    return (this.state?.currentRound || 0) + 1;
  }

  getTotalRounds(): number {
    return this.state?.totalRounds || 10;
  }

  quitGame(): void {
    this.stopWarDrums();
    this.miniGameService.finishMiniGame();
  }

  getResultText(result: { isCorrect: boolean } | null): string {
    if (!result) return '';
    if (result.isCorrect) {
      return this.currLang === 'ar' ? 'اسحب!' : 'PULL!';
    }
    return this.currLang === 'ar' ? 'خسارة!' : 'MISS!';
  }

  getQuestionText(question: GameQuestion | null): string {
    if (!question) return '';
    return this.currLang === 'ar' ? question.text.ar : question.text.en;
  }

  getOptionText(option: any): string {
    return this.currLang === 'ar' ? option.text.ar : option.text.en;
  }

  private arabicLabels: Record<string, string> = { 'A': 'أ', 'B': 'ب', 'C': 'ج', 'D': 'د' };

  getOptionLabel(id: string): string {
    return this.currLang === 'ar' ? (this.arabicLabels[id] || id) : id;
  }
}
